import React, { useState } from "react";
import { Link } from "react-router-dom";

import {
  MapPin,
  Mail,
  Phone,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Send,
  Clock3,
} from "lucide-react";

import toast from "react-hot-toast";
import api from "../api/api";

import { footerInfo, navigation } from "../control";

const LOGO_ONE = "/assets/images/logo1.png";
const LOGO_TWO = "/assets/images/logo2.png";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();

    if (!email) return;

    try {
      setLoading(true);

      await api.post("/newsletter/subscribe", {
        name,
        email,
        honeypot,
      });

      setSubscribed(true);

      setEmail("");
      setName("");
      setHoneypot("");

      toast.success("Thank you for subscribing!");

      setTimeout(() => {
        setSubscribed(false);
      }, 3000);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          error?.cleanMessage ||
          "Subscription failed"
      );
    } finally {
      setLoading(false);
    }
  };

  const portalFooterLinks = [
    {
      label: "Portal",
      path: "/portal",
    },
    {
      label: "FMS",
      path: "/portal/fms",
    },
  ];

  const cleanedFooterNavigation = navigation
    .filter(
      (n) =>
        n.path !== "/blog" &&
        n.path !== "/staff-mail"
    )
    .map((item) => {
      if (item.label === "Publications") {
        return {
          ...item,
          children:
            item.children?.filter(
              (child) =>
                child.label !== "OGFZA News" &&
                child.label !== "Media Gallery"
            ) || [],
        };
      }

      return item;
    });

  const middleFooterIndex = Math.ceil(
    cleanedFooterNavigation.length / 2
  );

  const updatedFooterNavigation = [
    ...cleanedFooterNavigation.slice(
      0,
      middleFooterIndex
    ),
    ...portalFooterLinks,
    ...cleanedFooterNavigation.slice(
      middleFooterIndex
    ),
  ];

  return (
    <footer className="bg-[#020f2e] text-slate-300 overflow-hidden">
      <div className="border-b border-white/10 bg-gradient-to-r from-[#201182] via-[#026B07] to-[#F2AF11] relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20" />

        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-[#026B07]/20 blur-[120px] rounded-full" />

        <div className="relative max-w-7xl mx-auto px-6 py-14 flex flex-col items-center justify-center text-center">
          <div className="flex items-center gap-5 mb-7">
            <div className="bg-white/95 backdrop-blur-md rounded-[24px] shadow-[0_20px_60px_rgba(0,0,0,0.35)] border border-white/40 p-4 md:p-5">
              <img
                src={LOGO_ONE}
                alt="OGFZA Logo One"
                className="w-[90px] md:w-[115px] h-[90px] md:h-[115px] object-contain"
              />
            </div>

            <div className="w-[2px] h-[80px] md:h-[100px] bg-gradient-to-b from-[#ffffff] via-[#F2AF11] to-[#026B07]" />

            <div className="bg-white/95 backdrop-blur-md rounded-[24px] shadow-[0_20px_60px_rgba(0,0,0,0.35)] border border-white/40 p-4 md:p-5">
              <img
                src={LOGO_TWO}
                alt="OGFZA Logo Two"
                className="w-[90px] md:w-[115px] h-[90px] md:h-[115px] object-contain"
              />
            </div>
          </div>

          <h2 className="text-4xl md:text-6xl font-black tracking-[-2px] text-white leading-tight">
            Oil & Gas Free Zones Authority
          </h2>

          <div className="w-32 h-[4px] rounded-full bg-gradient-to-r from-[#F2AF11] via-[#026B07] to-[#F2AF11] mt-5 mb-6" />

          <p className="max-w-4xl text-sm md:text-lg text-white/90 leading-relaxed font-medium">
            Nigeria’s premier regulatory authority driving investment,
            industrial development, logistics efficiency, economic
            expansion, and sustainable innovation across the nation’s
            oil and gas free zones ecosystem.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/free-zones"
              className="inline-flex items-center justify-center rounded-full bg-[#F2AF11] px-8 py-3 text-sm md:text-base font-black uppercase tracking-wide text-[#020f2e] shadow-2xl hover:bg-white hover:text-[#201182] transition-all duration-300"
            >
              Explore Free Zones
            </Link>

            <Link
              to="/portal"
              className="inline-flex items-center justify-center rounded-full border border-white/30 bg-white/10 backdrop-blur-md px-8 py-3 text-sm md:text-base font-black uppercase tracking-wide text-white shadow-2xl hover:bg-[#020f2e] hover:text-[#F2AF11] transition-all duration-300"
            >
              Access Portal
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div>
          <h3 className="text-white font-bold text-xl mb-6 relative pb-3 after:absolute after:left-0 after:bottom-0 after:w-12 after:h-[2px] after:bg-[#F2AF11]">
            About OGFZA
          </h3>

          <p className="text-sm leading-relaxed mb-6">
            The Oil & Gas Free Zones Authority
            (OGFZA) is Nigeria’s national
            regulatory agency responsible for
            supervising oil and gas free zones,
            promoting investment opportunities,
            facilitating trade, and supporting
            industrial development.
          </p>

          <div className="flex items-center gap-3 flex-wrap">
            {[
              {
                name: "facebook",
                Icon: Facebook,
                link: "https://www.facebook.com/oGFZA",
              },
              {
                name: "twitter",
                Icon: Twitter,
                link: "https://www.twitter.com/OGFZA",
              },
              {
                name: "linkedin",
                Icon: Linkedin,
                link: "https://ng.linkedin.com/company/ogfza",
              },
              {
                name: "instagram",
                Icon: Instagram,
                link: "https://instagram.com/OGFZA",
              },
              {
                name: "youtube",
                Icon: Send,
                link: "http://www.youtube.com/channel/UCK_ulDIl8S_InKt3d8Pbhmw/",
              },
            ].map(({ name, Icon, link }) => (
              <a
                key={name}
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#F2AF11] text-[#F2AF11] hover:text-[#201182] flex items-center justify-center transition-all duration-300"
              >
                <Icon size={17} />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-white font-bold text-xl mb-6 relative pb-3 after:absolute after:left-0 after:bottom-0 after:w-12 after:h-[2px] after:bg-[#F2AF11]">
            Quick Links
          </h3>

          <ul className="space-y-3 text-sm">
            {updatedFooterNavigation.map((n) => (
              <li key={n.label}>
                <Link
                  to={n.path}
                  className="hover:text-[#F2AF11] transition-colors flex items-center gap-2 before:content-['›'] before:text-[#F2AF11]"
                >
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="uppercase text-xs tracking-[3px] text-[#F2AF11] mb-3">
            Contact Information
          </p>

          <h3 className="text-white font-bold text-xl mb-6">
            Reach Out To Us
          </h3>

          <div className="space-y-5 text-sm">
            <div className="flex items-start gap-3">
              <MapPin
                size={18}
                className="flex-shrink-0 mt-1 text-[#F2AF11]"
              />

              <div>
                <p className="font-bold text-white mb-1">
                  Head Office
                </p>

                <p className="leading-relaxed">
                  Marble House, Federal Ocean
                  Terminal, Onne Port Complex,
                  Rivers State, Nigeria.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Mail
                size={18}
                className="flex-shrink-0 mt-1 text-[#F2AF11]"
              />

              <div>
                <p className="font-bold text-white mb-1">
                  Email Address
                </p>

                <p>
                  {footerInfo.emails.join(" / ")}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Phone
                size={18}
                className="flex-shrink-0 mt-1 text-[#F2AF11]"
              />

              <div>
                <p className="font-bold text-white mb-1">
                  Phone Numbers
                </p>

                <p>
                  {footerInfo.phones.join(" / ")}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock3
                size={18}
                className="flex-shrink-0 mt-1 text-[#F2AF11]"
              />

              <div>
                <p className="font-bold text-white mb-1">
                  Working Hours
                </p>

                <p>
                  Monday - Friday | 09:00am -
                  05:00pm
                </p>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-white font-bold text-xl mb-6 relative pb-3 after:absolute after:left-0 after:bottom-0 after:w-12 after:h-[2px] after:bg-[#F2AF11]">
            Newsletter
          </h3>

          <p className="text-sm leading-relaxed mb-5">
            Subscribe to receive the latest
            updates, news, publications, and
            announcements from OGFZA.
          </p>

          <form
            onSubmit={handleSubscribe}
            className="space-y-3"
          >
            <input
              type="text"
              value={honeypot}
              onChange={(e) =>
                setHoneypot(e.target.value)
              }
              className="hidden"
              autoComplete="off"
              tabIndex="-1"
            />

            <input
              type="text"
              value={name}
              onChange={(e) =>
                setName(e.target.value)
              }
              placeholder="Your Name"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 text-white placeholder:text-slate-400 focus:outline-none focus:border-[#F2AF11] text-sm rounded-md"
            />

            <div className="flex">
              <input
                type="email"
                value={email}
                onChange={(e) =>
                  setEmail(e.target.value)
                }
                placeholder="Your Email"
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 text-white placeholder:text-slate-400 focus:outline-none focus:border-[#F2AF11] text-sm rounded-l-md"
              />

              <button
                type="submit"
                disabled={loading}
                className="px-5 bg-[#F2AF11] hover:bg-[#026B07] text-white transition-all duration-300 rounded-r-md disabled:opacity-60"
              >
                <Send size={18} />
              </button>
            </div>
          </form>

          {subscribed && (
            <p className="text-[#F2AF11] text-xs mt-4 font-semibold">
              ✓ Thank you for subscribing!
            </p>
          )}
        </div>
      </div>

      <div className="border-t border-white/10 border-b border-white/10 bg-[#04122f]">
        <div className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {footerInfo.offices.map((office) => (
            <div key={office.title}>
              <h4 className="text-white font-bold mb-3">
                {office.title}
              </h4>

              <p className="text-sm leading-relaxed mb-2">
                {office.address}
              </p>

              <p className="text-sm text-[#F2AF11]">
                {office.phone}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#010814]">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col lg:flex-row items-center justify-between gap-4 text-xs text-slate-400">
          <p className="text-center lg:text-left">
            © {new Date().getFullYear()} Oil &
            Gas Free Zones Authority (OGFZA).
            All rights reserved.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/privacy"
              className="hover:text-[#F2AF11] transition-colors"
            >
              Privacy Policy
            </Link>

            <span className="text-white/20">
              |
            </span>

            <Link
              to="/terms"
              className="hover:text-[#F2AF11] transition-colors"
            >
              Site Terms
            </Link>

            <span className="text-white/20">
              |
            </span>

            <Link
              to="/disclaimer"
              className="hover:text-[#F2AF11] transition-colors"
            >
              Disclaimer
            </Link>

            <span className="text-white/20">
              |
            </span>

            <a
              href="https://fmiti.gov.ng/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#F2AF11] transition-colors"
            >
              FMITI
            </a>

            <span className="text-white/20">
              |
            </span>

            <a
              href="https://www.pebec.gov.ng/reportgov-ng"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#ffe21f] transition-colors font-semibold text-[#ffe21f]"
            >
              REPORT.GOV
            </a>

            <span className="text-white/20">
              |
            </span>

<a
  href="/contact-help/complaints"
  className="hover:text-[#F2AF11] transition-colors"
>
  Complaints
</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;